import { Navigate, Route, Routes } from 'react-router-dom';
import { RedirectIfAuthenticated, RequireAuth } from '@/features/auth/guards/RequireAuth';
import { RequireRole } from '@/features/auth/guards/RequireRole';
import { useAuth } from '@/features/auth/context/AuthContext';
import { EnrollPage } from '@/features/auth/pages/EnrollPage';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { TwoFactorPage } from '@/features/auth/pages/TwoFactorPage';
import { AlliesListPage } from '@/features/aliados/pages/AlliesListPage';
import { AllyDetailPage } from '@/features/aliados/pages/AllyDetailPage';
import { CreateAllyPage } from '@/features/aliados/pages/CreateAllyPage';
import { CreateStationPage } from '@/features/cargadores/pages/CreateStationPage';
import { StationDetailPage } from '@/features/cargadores/pages/StationDetailPage';
import { StationsListPage } from '@/features/cargadores/pages/StationsListPage';
import { CustomersListPage } from '@/features/clientes/pages/CustomersListPage';
import { CustomerDetailPage } from '@/features/clientes/pages/CustomerDetailPage';
import { CreateCustomerPage } from '@/features/clientes/pages/CreateCustomerPage';
import { ReportsPage } from '@/features/reportes/pages/ReportsPage';
import { CreateFleetPage } from '@/features/flotas/pages/CreateFleetPage';
import { FleetDetailPage } from '@/features/flotas/pages/FleetDetailPage';
import { FleetsListPage } from '@/features/flotas/pages/FleetsListPage';
import { LaboratorioPage } from '@/features/laboratorio/pages/LaboratorioPage';
import { BroadcastPage } from '@/features/dooh/pages/BroadcastPage';
import { MediaLibraryPage } from '@/features/dooh/pages/MediaLibraryPage';
import { ScreenDetailPage } from '@/features/dooh/pages/ScreenDetailPage';
import { ScreensPage } from '@/features/dooh/pages/ScreensPage';
import { CreateLocationPage } from '@/features/locaciones/pages/CreateLocationPage';
import { LocationDetailPage } from '@/features/locaciones/pages/LocationDetailPage';
import { LocationsListPage } from '@/features/locaciones/pages/LocationsListPage';
import { CreateMembershipPage } from '@/features/membresias/pages/CreateMembershipPage';
import { EditMembershipPage } from '@/features/membresias/pages/EditMembershipPage';
import { MembershipDetailPage } from '@/features/membresias/pages/MembershipDetailPage';
import { MembershipsListPage } from '@/features/membresias/pages/MembershipsListPage';
import { NocPage } from '@/features/noc/pages/NocPage';
import { CreatePackPage } from '@/features/packs/pages/CreatePackPage';
import { EditPackPage } from '@/features/packs/pages/EditPackPage';
import { PackDetailPage } from '@/features/packs/pages/PackDetailPage';
import { PacksListPage } from '@/features/packs/pages/PacksListPage';
import { CreateTariffPage } from '@/features/tarifas/pages/CreateTariffPage';
import { EditTariffPage } from '@/features/tarifas/pages/EditTariffPage';
import { TariffDetailPage } from '@/features/tarifas/pages/TariffDetailPage';
import { TariffsListPage } from '@/features/tarifas/pages/TariffsListPage';
import { AdminLayout } from '@/layouts/AdminLayout';
import {
  ALIADOS_ROLES,
  ALIADOS_WRITE_ROLES,
  CARGADORES_ROLES,
  CLIENTES_ROLES,
  CLIENTES_WRITE_ROLES,
  DOOH_MEDIA_ROLES,
  FLOTAS_ROLES,
  FLOTAS_WRITE_ROLES,
  DOOH_SCREENS_ROLES,
  LOCACIONES_ROLES,
  LOCACIONES_WRITE_ROLES,
  MEMBERSHIPS_ROLES,
  MEMBERSHIPS_WRITE_ROLES,
  NOC_SECTION_ROLES,
  REPORTES_ROLES,
  PACKS_ROLES,
  PACKS_WRITE_ROLES,
  TARIFAS_ROLES,
  TARIFAS_WRITE_ROLES,
  defaultLandingPath,
} from '@/layouts/navigation';
import { NotFoundPage } from './NotFoundPage';

/**
 * Redirect del `index` según rol: manda al primer ítem visible del menú. Sin esto,
 * un rol que no ve el NOC (p. ej. MARKETING) aterrizaría en un 403 con sidebar vacío.
 */
function IndexRedirect() {
  const { user } = useAuth();
  return <Navigate to={defaultLandingPath(user?.role)} replace />;
}

/**
 * Rutas del Admin Web.
 * - Públicas (solo sin sesión): login y flujo 2FA.
 * - Privadas (guard de sesión): shell con sidebar (`/noc`, `/laboratorio`, `/aliados`,
 *   `/ubicaciones`, `/cargadores`, `/clientes`, `/flotas`, `/tarifas`, `/packs`,
 *   `/membresias`).
 * - Privadas por rol: cada sección exige los roles declarados en el contrato.
 */
export function AppRouter() {
  return (
    <Routes>
      <Route element={<RedirectIfAuthenticated />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/2fa" element={<TwoFactorPage />} />
        <Route path="/login/enroll" element={<EnrollPage />} />
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<AdminLayout />}>
          <Route index element={<IndexRedirect />} />
          <Route element={<RequireRole roles={NOC_SECTION_ROLES} />}>
            <Route path="noc" element={<NocPage />} />
            <Route path="laboratorio" element={<LaboratorioPage />} />
          </Route>
          <Route element={<RequireRole roles={ALIADOS_WRITE_ROLES} />}>
            <Route path="aliados/nuevo" element={<CreateAllyPage />} />
          </Route>
          <Route element={<RequireRole roles={ALIADOS_ROLES} />}>
            <Route path="aliados" element={<AlliesListPage />} />
            <Route path="aliados/:allyId" element={<AllyDetailPage />} />
          </Route>
          <Route element={<RequireRole roles={CLIENTES_WRITE_ROLES} />}>
            <Route path="clientes/nuevo" element={<CreateCustomerPage />} />
          </Route>
          <Route element={<RequireRole roles={CLIENTES_ROLES} />}>
            <Route path="clientes" element={<CustomersListPage />} />
            <Route path="clientes/:customerId" element={<CustomerDetailPage />} />
          </Route>
          <Route element={<RequireRole roles={FLOTAS_WRITE_ROLES} />}>
            <Route path="flotas/nuevo" element={<CreateFleetPage />} />
          </Route>
          <Route element={<RequireRole roles={FLOTAS_ROLES} />}>
            <Route path="flotas" element={<FleetsListPage />} />
            <Route path="flotas/:fleetId" element={<FleetDetailPage />} />
          </Route>
          <Route element={<RequireRole roles={DOOH_MEDIA_ROLES} />}>
            <Route path="dooh/medios" element={<MediaLibraryPage />} />
            <Route path="dooh/emision" element={<BroadcastPage />} />
          </Route>
          <Route element={<RequireRole roles={DOOH_SCREENS_ROLES} />}>
            <Route path="dooh/pantallas" element={<ScreensPage />} />
            <Route path="dooh/pantallas/:screenId" element={<ScreenDetailPage />} />
          </Route>
          <Route element={<RequireRole roles={LOCACIONES_WRITE_ROLES} />}>
            <Route path="ubicaciones/nueva" element={<CreateLocationPage />} />
          </Route>
          <Route element={<RequireRole roles={LOCACIONES_ROLES} />}>
            <Route path="ubicaciones" element={<LocationsListPage />} />
            <Route path="ubicaciones/:locationId" element={<LocationDetailPage />} />
          </Route>
          <Route element={<RequireRole roles={CARGADORES_ROLES} />}>
            <Route path="cargadores" element={<StationsListPage />} />
            <Route path="cargadores/nuevo" element={<CreateStationPage />} />
            <Route path="cargadores/:stationId" element={<StationDetailPage />} />
          </Route>
          <Route element={<RequireRole roles={REPORTES_ROLES} />}>
            <Route path="reportes" element={<ReportsPage />} />
          </Route>
          <Route element={<RequireRole roles={TARIFAS_WRITE_ROLES} />}>
            <Route path="tarifas/nueva" element={<CreateTariffPage />} />
            <Route path="tarifas/:tariffId/editar" element={<EditTariffPage />} />
          </Route>
          <Route element={<RequireRole roles={TARIFAS_ROLES} />}>
            <Route path="tarifas" element={<TariffsListPage />} />
            <Route path="tarifas/:tariffId" element={<TariffDetailPage />} />
          </Route>
          <Route element={<RequireRole roles={PACKS_WRITE_ROLES} />}>
            <Route path="packs/nuevo" element={<CreatePackPage />} />
            <Route path="packs/:packId/editar" element={<EditPackPage />} />
          </Route>
          <Route element={<RequireRole roles={PACKS_ROLES} />}>
            <Route path="packs" element={<PacksListPage />} />
            <Route path="packs/:packId" element={<PackDetailPage />} />
          </Route>
          <Route element={<RequireRole roles={MEMBERSHIPS_WRITE_ROLES} />}>
            <Route path="membresias/nueva" element={<CreateMembershipPage />} />
            <Route
              path="membresias/:membershipId/editar"
              element={<EditMembershipPage />}
            />
          </Route>
          <Route element={<RequireRole roles={MEMBERSHIPS_ROLES} />}>
            <Route path="membresias" element={<MembershipsListPage />} />
            <Route
              path="membresias/:membershipId"
              element={<MembershipDetailPage />}
            />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
